import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import ReportDateRangeButtons from '@/components/reports/ReportDateRangeButtons';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import {
  TrendingUp,
  Receipt,
  ShoppingCart,
  Wrench,
  Menu,
  Award,
  Sparkles,
  AlertTriangle,
  X,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  LabelList,
} from 'recharts';

import type { PosSettings } from '@/types';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import { formatQuickInsight } from '@/utils/formatQuickInsight';

const currencyAxisTick = (v: number) => `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
    const showTotal = payload.length > 1;
    return (
      <div className="bg-gray-900/95 backdrop-blur-md border border-gray-800 rounded-xl p-3.5 shadow-xl text-xs min-w-[180px]">
        <p className="font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">{label}</p>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color || entry.fill }} />
                <span className="text-gray-300 font-medium truncate">{entry.name}</span>
              </div>
              <span className="text-white font-bold shrink-0">
                {formatMoney(typeof entry.value === 'number' ? entry.value : Number(entry.value) || 0)}
              </span>
            </div>
          ))}
          {showTotal && (
            <div className="border-t border-gray-800 pt-1.5 mt-1.5 flex items-center justify-between gap-4">
              <span className="text-gray-400 font-semibold uppercase tracking-wider">Total</span>
              <span className="text-emerald-400 font-extrabold">
                {formatMoney(total)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

const getAvatarName = (name: string) => {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getAvatarBg = (name: string) => {
  const colors = [
    'bg-blue-50 text-blue-600',
    'bg-emerald-50 text-emerald-600',
    'bg-purple-50 text-purple-600',
    'bg-amber-50 text-amber-600',
    'bg-pink-50 text-pink-600',
    'bg-indigo-50 text-indigo-600',
    'bg-cyan-50 text-cyan-600',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  const [activeTab, setActiveTab] = useState<'performance' | 'ai'>('performance');
  const [activeModal, setActiveModal] = useState<'reorder' | 'dead_stock' | 'pricing' | null>(null);
  const [showAllOutstandingModal, setShowAllOutstandingModal] = useState(false);
  const [showAllPayableModal, setShowAllPayableModal] = useState(false);
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as PosSettings;
    },
  });

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['reportSummary', from_date, to_date, dateRange],
    queryFn: async () => {
      const res = await posApi.getReportSummary(from_date, to_date, dateRange);
      return res.data.message;
    },
  });

  const summary = reportData?.summary || {};
  const salesTrend = reportData?.salesTrend || [];
  const categoryBreakdown = reportData?.categoryBreakdown || [];
  const topSelling = reportData?.topSelling || [];
  const topSellingServices = reportData?.topSellingServices || [];
  const reorderSuggestions = reportData?.reorderSuggestions || [];
  const deadStockAlerts = reportData?.deadStockAlerts || [];
  const pricingSuggestions = reportData?.pricingSuggestions || [];
  const quickInsights = reportData?.quickInsights || [];
  const paymentTrend = reportData?.paymentTrend || [];
  const paymentMethods = reportData?.paymentMethods || [];
  const salesmanTrend = reportData?.salesmanTrend || [];
  const salesmen = reportData?.salesmen || [];
  const customerOutstanding = reportData?.customerOutstanding || [];
  const supplierPayable = reportData?.supplierPayable || [];
  // const highReturning = reportData?.highReturning || []; // kept for future use

  const stats = [
    {
      key: 'totalSales',
      label: t('dashboard.totalSales'),
      value: formatMoney(summary.total_sales || 0),
      icon: PakRupeeIcon,
      gradient: 'from-emerald-500 to-teal-600',
      shadow: 'shadow-emerald-100',
    },
    {
      key: 'invoices',
      label: t('dashboard.invoices'),
      value: (summary.invoice_count || 0).toLocaleString(),
      icon: Receipt,
      gradient: 'from-blue-500 to-indigo-600',
      shadow: 'shadow-blue-100',
    },
    {
      key: 'productSales',
      label: t('dashboard.productSales'),
      value: formatMoney(summary.item_sales || 0),
      icon: ShoppingCart,
      gradient: 'from-amber-500 to-orange-600',
      shadow: 'shadow-amber-100',
    },
    ...(!posSettings?.sell_only_products ? [{
      key: 'serviceSales',
      label: t('dashboard.serviceSales'),
      value: formatMoney(summary.service_sales || 0),
      icon: Wrench,
      gradient: 'from-violet-500 to-purple-600',
      shadow: 'shadow-purple-100',
    }] : []),
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        <div className="p-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('analytics.title')}</h1>
                <p className="text-gray-500 text-sm">{t('analytics.subtitle')}</p>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex border-b border-gray-200 gap-4 mb-2">
            <button
              onClick={() => setActiveTab('performance')}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'performance'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('analytics.performance')}
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'ai'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Sparkles className={`w-4 h-4 ${activeTab === 'ai' ? 'text-purple-600 animate-pulse' : 'text-gray-400'}`} />
              {t('analytics.aiInsights')}
            </button>
          </div>

          {isLoading ? (
            <div className="card p-12 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <>
              {activeTab === 'performance' && (
                <section className="space-y-4">
                  <div className="card p-5">
                    <ReportDateRangeButtons
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                      customFrom={customFrom}
                      customTo={customTo}
                      onCustomFromChange={setCustomFrom}
                      onCustomToChange={setCustomTo}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {stats.map((s) => (
                      <div key={s.key} className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-50/10 to-gray-100/30 rounded-bl-full pointer-events-none group-hover:scale-110 transition-transform duration-300" />
                        <div className="flex items-center gap-5">
                          <div className={`w-12 h-12 bg-gradient-to-r ${s.gradient} ${s.shadow} shadow-lg rounded-xl flex items-center justify-center shrink-0`}>
                            <s.icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{s.label}</p>
                            <p className="text-2xl font-bold text-gray-900 mt-1 truncate">{s.value}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                      <h3 className="font-bold text-gray-800 text-lg mb-6">{t('analytics.salesTrend')}</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        {salesTrend.length > 0 ? (
                          <BarChart data={salesTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={currencyAxisTick} padding={{ top: 50 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                            <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="products" stackId="a" fill="#059669" name={t('nav.products')} radius={[0, 0, 0, 0]}>
                              {posSettings?.sell_only_products && (
                                <LabelList dataKey="sales" position="top" formatter={(v: any) => typeof v === 'number' ? v.toLocaleString() : v} style={{ fontSize: 10, fill: '#475569', fontWeight: 'bold' }} angle={-90} offset={20} />
                              )}
                            </Bar>
                            {!posSettings?.sell_only_products && (
                              <Bar dataKey="services" stackId="a" fill="#4f46e5" name={t('pos.services')} radius={[4, 4, 0, 0]}>
                                <LabelList dataKey="sales" position="top" formatter={(v: any) => typeof v === 'number' ? v.toLocaleString() : v} style={{ fontSize: 10, fill: '#475569', fontWeight: 'bold' }} angle={-90} offset={20} />
                              </Bar>
                            )}
                          </BarChart>
                        ) : (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            {t('analytics.noData')}
                          </div>
                        )}
                      </ResponsiveContainer>
                    </div>

                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                      <h3 className="font-bold text-gray-800 text-lg mb-6">{t('analytics.salesByCategory')}</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        {categoryBreakdown.length > 0 ? (
                          <BarChart data={categoryBreakdown} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#64748b' }} width={100} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                            <Bar dataKey="value" fill="#0284c7" name={t('reports.revenue')} radius={[0, 6, 6, 0]} />
                          </BarChart>
                        ) : (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            {t('analytics.noData')}
                          </div>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className={`grid grid-cols-1 ${posSettings?.sell_only_products ? '' : 'lg:grid-cols-2'} gap-6`}>
                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                      <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center gap-2">
                        <Award className="w-5 h-5 text-amber-500" />
                        {t('analytics.topProducts')}
                      </h3>
                      <div className="overflow-auto max-h-[270px] pr-1 space-y-4">
                        {topSelling.length > 0 ? (
                          <div className="space-y-4">
                            {topSelling.map((prod: { name: string; quantity: number }, idx: number) => {
                              const maxQty = topSelling[0]?.quantity || 1;
                              const pct = (prod.quantity / maxQty) * 100;
                              return (
                                <div key={idx} className="flex flex-col gap-1.5 py-1">
                                  <div className="flex justify-between text-sm">
                                    <span className="font-semibold text-gray-700">{prod.name}</span>
                                    <span className="font-bold text-emerald-600">{prod.quantity.toLocaleString()} units</span>
                                  </div>
                                  <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                    <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center text-gray-400 py-8">{t('analytics.noProductsSold')}</div>
                        )}
                      </div>
                    </div>

                    {!posSettings?.sell_only_products && (
                      <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                        <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center gap-2">
                          <Wrench className="w-5 h-5 text-indigo-500" />
                          {t('analytics.topServices')}
                        </h3>
                        <div className="overflow-auto max-h-[270px] pr-1 space-y-4">
                          {topSellingServices.length > 0 ? (
                            <div className="space-y-4">
                              {topSellingServices.map((svc: { name: string; quantity: number }, idx: number) => {
                                const maxQty = topSellingServices[0]?.quantity || 1;
                                const pct = (svc.quantity / maxQty) * 100;
                                return (
                                  <div key={idx} className="flex flex-col gap-1.5 py-1">
                                    <div className="flex justify-between text-sm">
                                      <span className="font-semibold text-gray-700">{svc.name}</span>
                                      <span className="font-bold text-indigo-600">{svc.quantity.toLocaleString()} units</span>
                                    </div>
                                    <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                      <div className="bg-indigo-500 h-full rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-center text-gray-400 py-8">{t('analytics.noServicesSold')}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                      <h3 className="font-bold text-gray-800 text-lg mb-6">{t('analytics.paymentMethods')}</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        {paymentTrend.length > 0 ? (
                          <BarChart data={paymentTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={currencyAxisTick} padding={{ top: 50 }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                            <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                            {paymentMethods.map((pm: string, idx: number) => {
                              const colors = ['#0d9488', '#0284c7', '#d97706', '#475569', '#b91c1c', '#4f46e5', '#db2777'];
                              const color = colors[idx % colors.length];
                              return (
                                <Bar key={pm} dataKey={pm} stackId="p" fill={color} name={pm} />
                              );
                            })}
                          </BarChart>
                        ) : (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            {t('analytics.noData')}
                          </div>
                        )}
                      </ResponsiveContainer>
                    </div>

                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300">
                      <h3 className="font-bold text-gray-800 text-lg mb-6">{t('analytics.salesmanPerformance')}</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        {salesmanTrend.length > 0 ? (
                          <BarChart data={salesmanTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={currencyAxisTick} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                            <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                            {salesmen.map((sm: string, idx: number) => {
                              const colors = ['#4f46e5', '#0d9488', '#db2777', '#d97706', '#0284c7', '#7c3aed', '#b91c1c'];
                              const color = colors[idx % colors.length];
                              return <Bar key={sm} dataKey={sm} stackId="s" fill={color} name={sm} />;
                            })}
                          </BarChart>
                        ) : (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            {t('analytics.noData')}
                          </div>
                        )}
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300 flex flex-col h-80">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-800 text-lg">{t('analytics.customerOutstanding')}</h3>
                        {customerOutstanding.length > 5 && (
                          <button onClick={() => setShowAllOutstandingModal(true)} className="text-xs font-bold text-primary-600 hover:text-primary-700 transition-colors uppercase tracking-wider bg-primary-50 px-2.5 py-1.5 rounded-lg">
                            {t('analytics.viewAll')}
                          </button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto pr-1">
                        {customerOutstanding.length > 0 ? (
                          <div className="space-y-3">
                            {customerOutstanding.slice(0, 5).map((co: any) => (
                              <div key={co.customer_name} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 px-2 rounded-xl transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${getAvatarBg(co.customer_name)}`}>
                                    {getAvatarName(co.customer_name)}
                                  </div>
                                  <p className="text-sm font-semibold text-gray-700 truncate">{co.customer_name}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-extrabold text-red-600">{formatMoney(co.outstanding)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            No outstanding customer balances.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="relative overflow-hidden bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300 flex flex-col h-80">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-gray-800 text-lg">{t('analytics.suppliersPayable')}</h3>
                        {supplierPayable.length > 5 && (
                          <button onClick={() => setShowAllPayableModal(true)} className="text-xs font-bold text-primary-600 hover:text-primary-700 transition-colors uppercase tracking-wider bg-primary-50 px-2.5 py-1.5 rounded-lg">
                            {t('analytics.viewAll')}
                          </button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto pr-1">
                        {supplierPayable.length > 0 ? (
                          <div className="space-y-3">
                            {supplierPayable.slice(0, 5).map((sp: any) => (
                              <div key={sp.supplier_name} className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 px-2 rounded-xl transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${getAvatarBg(sp.supplier_name)}`}>
                                    {getAvatarName(sp.supplier_name)}
                                  </div>
                                  <p className="text-sm font-semibold text-gray-700 truncate">{sp.supplier_name}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-extrabold text-amber-600">{formatMoney(sp.payable)}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-sm text-gray-400">
                            No outstanding supplier payables.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {activeTab === 'ai' && (
                <div className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
                    <h2 className="text-lg font-bold text-gray-900">{t('analytics.aiTitle')}</h2>
                    <span className="text-[10px] px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full font-bold uppercase tracking-wider">
                      {t('analytics.liveAnalysis')}
                    </span>
                  </div>

                  {/* Quick Insights Sparkle Bar */}
                  {quickInsights && quickInsights.length > 0 && (
                    <div className="card p-5 bg-gradient-to-r from-purple-50 via-indigo-50/30 to-purple-50/50 border border-purple-100 rounded-2xl">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <h4 className="font-semibold text-sm text-purple-900 uppercase tracking-wider">{t('analytics.quickInsights')}</h4>
                      </div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {quickInsights.map((insight: { text?: string; code?: string; type: 'positive' | 'negative' | 'neutral'; params?: Record<string, unknown> }, idx: number) => {
                          const isPositive = insight.type === 'positive';
                          const isNegative = insight.type === 'negative';
                          
                          let bgClass = "bg-sky-50/60 border-sky-100/80";
                          let textClass = "text-sky-950";
                          let iconClass = "text-sky-500";
                          
                          if (isPositive) {
                            bgClass = "bg-emerald-50/60 border-emerald-100/80";
                            textClass = "text-emerald-950";
                            iconClass = "text-emerald-500";
                          } else if (isNegative) {
                            bgClass = "bg-rose-50/60 border-rose-100/80";
                            textClass = "text-rose-950";
                            iconClass = "text-rose-500";
                          }
                          
                          return (
                            <li key={idx} className={`flex items-start gap-2 p-3 rounded-xl border shadow-sm transition-all duration-200 hover:scale-[1.01] ${bgClass}`}>
                              <Sparkles className={`w-4 h-4 shrink-0 mt-0.5 ${iconClass}`} />
                              <span className={`font-medium ${textClass}`}>{formatQuickInsight(t, insight)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* Three main AI Predictive Cards */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    
                    {/* 1. Smart Reorder Suggestions */}
                    <div className="card p-5 border border-slate-100 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <ShoppingCart className="w-4 h-4 text-orange-500" />
                            <h3 className="font-bold text-gray-900 text-sm tracking-tight">{t('analytics.smartReorder')}</h3>
                          </div>
                          <span className="text-[9px] px-2 py-0.5 bg-purple-100 text-purple-800 font-bold rounded-full uppercase">AI</span>
                        </div>

                        <div className="space-y-4">
                          {reorderSuggestions && reorderSuggestions.length > 0 ? (
                            reorderSuggestions.slice(0, 3).map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-xl border border-slate-100">
                                <div>
                                  <p className="font-semibold text-xs text-slate-800">{item.item_name}</p>
                                  <div className="flex items-center gap-2 mt-1 text-[11px]">
                                    <span className={item.current_stock <= 0 ? "text-red-500 font-bold" : "text-slate-500"}>
                                      {t('analytics.stock')}: {item.current_stock}
                                    </span>
                                    <span className="text-slate-400">•</span>
                                    <span className="text-slate-500">{t('analytics.weeklySale')}: {item.avg_weekly_sale}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="block text-[10px] text-slate-400">{t('analytics.suggested')}</span>
                                  <span className="font-bold text-emerald-600 text-sm">{item.suggested_qty}</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-slate-400 text-center py-6">{t('analytics.noReorderNeeded')}</p>
                          )}
                        </div>
                      </div>
                      {reorderSuggestions && reorderSuggestions.length > 0 && (
                        <div className="pt-4 mt-2 border-t border-slate-100 flex items-center justify-between">
                          <button
                            onClick={() => setActiveModal('reorder')}
                            className="text-xs text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1"
                          >
                            {t('analytics.viewAllSuggestions')}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* 2. Dead Stock & Slow Moving Alerts */}
                    <div className="card p-5 border border-slate-100 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-rose-500" />
                            <h3 className="font-bold text-gray-900 text-sm tracking-tight">{t('analytics.deadStock')}</h3>
                          </div>
                          <span className="text-[9px] px-2 py-0.5 bg-purple-100 text-purple-800 font-bold rounded-full uppercase">AI</span>
                        </div>

                        <div className="space-y-4">
                          {deadStockAlerts && deadStockAlerts.length > 0 ? (
                            deadStockAlerts.slice(0, 3).map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-xl border border-slate-100">
                                <div>
                                  <p className="font-semibold text-xs text-slate-800">{item.item_name}</p>
                                  <div className="flex items-center gap-2 mt-1 text-[11px]">
                                    <span className="text-slate-500">{t('analytics.inactive')}: {item.no_sale_days} {t('analytics.days')}</span>
                                    <span className="text-slate-400">•</span>
                                    <span className="text-slate-500">{t('analytics.stock')}: {item.current_stock}</span>
                                  </div>
                                </div>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  item.status === 'Dead Stock' 
                                    ? 'bg-red-50 text-red-600 border border-red-200' 
                                    : 'bg-amber-50 text-amber-600 border border-amber-200'
                                }`}>
                                  {item.status}
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-slate-400 text-center py-6">{t('analytics.healthyVelocity')}</p>
                          )}
                        </div>
                      </div>
                      {deadStockAlerts && deadStockAlerts.length > 0 && (
                        <div className="pt-4 mt-2 border-t border-slate-100 flex items-center justify-between">
                          <button
                            onClick={() => setActiveModal('dead_stock')}
                            className="text-xs text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1"
                          >
                            {t('analytics.viewAll')} →
                          </button>
                        </div>
                      )}
                    </div>

                    {/* 3. AI Profit & Pricing Assistant */}
                    <div className="card p-5 border border-slate-100 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-emerald-500" />
                            <h3 className="font-bold text-gray-900 text-sm tracking-tight">{t('analytics.pricingAssistant')}</h3>
                          </div>
                          <span className="text-[9px] px-2 py-0.5 bg-purple-100 text-purple-800 font-bold rounded-full uppercase">AI</span>
                        </div>

                        <div className="space-y-4">
                          {pricingSuggestions && pricingSuggestions.length > 0 ? (
                            pricingSuggestions.slice(0, 3).map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50/80 rounded-xl border border-slate-100">
                                <div>
                                  <p className="font-semibold text-xs text-slate-800">{item.item_name}</p>
                                  <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500">
                                    <span>{t('analytics.cost')}: {formatMoney(Number(item.cost_price))}</span>
                                    <span>•</span>
                                    <span>{t('analytics.current')}: {formatMoney(Number(item.current_price))}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className="block text-[10px] text-slate-400">{t('analytics.recPrice')}</span>
                                  <span className="font-bold text-emerald-600 text-xs">{formatMoney(Number(item.recommended_price))}</span>
                                  <span className="block text-[9px] text-slate-500">{t('analytics.margin')}: {item.recommended_margin}%</span>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-slate-400 text-center py-6">{t('analytics.pricesOptimized')}</p>
                          )}
                        </div>
                      </div>
                      {pricingSuggestions && pricingSuggestions.length > 0 && (
                        <div className="pt-4 mt-2 border-t border-slate-100 flex items-center justify-between">
                          <button
                            onClick={() => setActiveModal('pricing')}
                            className="text-xs text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1"
                          >
                            {t('analytics.viewPricingSuggestions')}
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}
            </>
          )}

          {/* Modal for viewing all predictions details */}
          {activeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
              <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
                
                {/* Modal Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-gray-900 text-lg">
                      {activeModal === 'reorder' && t('analytics.smartReorder')}
                      {activeModal === 'dead_stock' && t('analytics.deadStock')}
                      {activeModal === 'pricing' && t('analytics.pricingAssistant')}
                    </h3>
                  </div>
                  <button 
                    onClick={() => setActiveModal(null)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <span className="text-xl font-bold">&times;</span>
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 overflow-y-auto space-y-4 flex-1">
                  {activeModal === 'reorder' && (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500 mb-2">{t('analytics.reorderHelp')}</p>
                      {reorderSuggestions.map((item: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                          <div>
                            <p className="font-bold text-sm text-slate-800">{item.item_name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {t('analytics.currentStock')}: <span className={item.current_stock <= 0 ? "text-red-500 font-bold" : "text-slate-700"}>{item.current_stock}</span> | {t('analytics.weeklySalesRate')}: {item.avg_weekly_sale}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="block text-[11px] text-slate-400 font-medium">{t('analytics.suggestedReorder')}</span>
                            <span className="font-extrabold text-emerald-600 text-base">{item.suggested_qty}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeModal === 'dead_stock' && (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500 mb-2">{t('analytics.healthyVelocity')}</p>
                      {deadStockAlerts.map((item: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                          <div>
                            <p className="font-bold text-sm text-slate-800">{item.item_name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {t('analytics.inStock')}: {item.current_stock} | {t('analytics.inactive')}: {item.no_sale_days} {t('analytics.days')}
                            </p>
                          </div>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                            item.status === 'Dead Stock' 
                              ? 'bg-red-100 text-red-700 border border-red-200' 
                              : 'bg-amber-100 text-amber-700 border border-amber-200'
                          }`}>
                            {item.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeModal === 'pricing' && (
                    <div className="space-y-3">
                      <p className="text-xs text-slate-500 mb-2">{t('analytics.pricingAssistant')}</p>
                      {pricingSuggestions.map((item: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                          <div>
                            <p className="font-bold text-sm text-slate-800">{item.item_name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {t('analytics.costPrice')}: {formatMoney(Number(item.cost_price))} | {t('analytics.currentRetail')}: {formatMoney(Number(item.current_price))} ({t('analytics.margin')}: {item.margin}%)
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="block text-[11px] text-slate-400 font-medium">{t('analytics.recRetail')}</span>
                            <span className="font-extrabold text-emerald-600 text-base">{formatMoney(Number(item.recommended_price))}</span>
                            <span className="block text-[10px] text-emerald-700 font-semibold">{t('analytics.margin')}: {item.recommended_margin}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                  <button
                    onClick={() => setActiveModal(null)}
                    className="px-5 py-2 text-sm font-semibold bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* New View All Customer Outstanding Modal */}
          {showAllOutstandingModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
              <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-bold text-gray-900 text-lg">{t('analytics.customerBalances')}</h3>
                  <button 
                    onClick={() => setShowAllOutstandingModal(false)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto divide-y divide-slate-100 flex-1">
                  {customerOutstanding.map((co: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between py-3">
                      <p className="font-semibold text-sm text-slate-800">{co.customer_name}</p>
                      <p className="font-bold text-sm text-red-600">{formatMoney(co.outstanding)}</p>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                  <button
                    onClick={() => setShowAllOutstandingModal(false)}
                    className="px-5 py-2 text-sm font-semibold bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New View All Suppliers Payable Modal */}
          {showAllPayableModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
              <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-bold text-gray-900 text-lg">{t('analytics.supplierBalances')}</h3>
                  <button 
                    onClick={() => setShowAllPayableModal(false)}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto divide-y divide-slate-100 flex-1">
                  {supplierPayable.map((sp: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between py-3">
                      <p className="font-semibold text-sm text-slate-800">{sp.supplier_name}</p>
                      <p className="font-bold text-sm text-amber-600">{formatMoney(sp.payable)}</p>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                  <button
                    onClick={() => setShowAllPayableModal(false)}
                    className="px-5 py-2 text-sm font-semibold bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow transition-colors"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
